import { ApplicationResponseStatus } from 'generatedTypes/flight/models';
import { useNavigate } from 'react-router-dom';
import { formatToUSTime } from 'utils/dateTimeUtils/dateFormatUtil';
import { formatDurationSeconds } from 'utils/dateTimeUtils/durationUtil';

import { HorizontalContainer, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import ApplicationsIcon from 'components/icons/ApplicationsIcon';
import TopRightArrowIcon from 'components/icons/TopRightArrowIcon';
import CaptionText from 'components/text/CaptionText';
import LabelText from 'components/text/LabelText';
import ParagraphText from 'components/text/ParagraphText';

import useTranslation from './hooks/useTranslation';

import DetailsViewCardApplicationListItemSkeleton from './components/Skeleton';
import { TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const DetailsViewCardApplicationListItem: FunctionComponent<Props> = ({
    application,
    applicationRoute,
    className = '',
    isActive = false,
    isLoading = false,
    onClick,
    testId = TEST_ID
}) => {
    const navigate = useNavigate();
    const { DURATION, END, FLIGHTS, START, STATUS_LABELS } = useTranslation();

    if (isLoading || !application) {
        return (
            <Wrapper
                className={`${className} ${styles.detailsViewCardApplicationListItem}`}
                testId={testId}>
                <DetailsViewCardApplicationListItemSkeleton />
            </Wrapper>
        );
    }

    const { endTime, name, startTime, status, totalFlightsDurationSeconds, totalNumberOfFlights } =
        application;

    const formattedStart = startTime ? formatToUSTime(new Date(startTime)) : '';
    const formattedEnd = endTime ? formatToUSTime(new Date(endTime)) : '';
    const durationText = formatDurationSeconds(totalFlightsDurationSeconds ?? 0);
    const showMetadata = status !== ApplicationResponseStatus.NOT_STARTED;

    const activeClass = isActive ? styles.active : styles.inactive;
    const showNavigation = isActive && applicationRoute;

    const handleNavigate = () => {
        navigate(applicationRoute!);
    };

    return (
        <Wrapper
            className={`${className} ${activeClass} ${styles.detailsViewCardApplicationListItem}`}
            onClick={onClick}
            testId={testId}>
            <HorizontalContainer className={styles.horizontalContainerWrapper}>
                <HorizontalContainer className={styles.horizontalContainer}>
                    <Wrapper className={styles.wrapper}>
                        <ApplicationsIcon className={styles.icon} />
                    </Wrapper>

                    <VerticalContainer className={styles.verticalContainer}>
                        <ParagraphText className={styles.paragraphText} text={name} />

                        {status && <CaptionText text={STATUS_LABELS[status]} />}

                        {showMetadata && (
                            <Wrapper className={styles.wrapperData}>
                                <LabelText
                                    text={`${START} ${formattedStart} • ${END} ${formattedEnd}`}
                                />

                                <HorizontalContainer>
                                    <LabelText text={DURATION} />

                                    <LabelText text={durationText} />

                                    <LabelText text={`• ${FLIGHTS}`} />

                                    <LabelText text={`${totalNumberOfFlights ?? 0}`} />
                                </HorizontalContainer>
                            </Wrapper>
                        )}
                    </VerticalContainer>
                </HorizontalContainer>

                {showNavigation && (
                    <Wrapper className={styles.wrapperNavigation} onClick={handleNavigate}>
                        <TopRightArrowIcon />
                    </Wrapper>
                )}
            </HorizontalContainer>
        </Wrapper>
    );
};

export default DetailsViewCardApplicationListItem;
